<template>
  <span
    :class="['ui-select', `ui-select--${size}`, { 'is-invalid': invalid, 'is-disabled': disabled }]"
  >
    <select
      v-model="model"
      class="ui-select__field"
      :disabled="disabled"
      :aria-invalid="invalid || undefined"
      v-bind="$attrs"
    >
      <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
      <slot />
    </select>
    <svg class="ui-select__chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path
        d="M2 3.5l3 3 3-3"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </span>
</template>

<script setup lang="ts">
/**
 * A choice among a few known values, in a native select drawn as the kit's
 * Input: the same border, height and focus ring, a chevron where the
 * browser's arrow was. Native on purpose: it opens the platform's own list,
 * which every keyboard and screen reader already knows.
 *
 * The options are `<option>` elements in the default slot.
 */
defineOptions({ inheritAttrs: false })

withDefaults(
  defineProps<{
    size?: 'sm' | 'md'
    /** Shown, and not choosable, while nothing is chosen. */
    placeholder?: string
    invalid?: boolean
    disabled?: boolean
  }>(),
  { size: 'md', placeholder: '', invalid: false, disabled: false },
)

const model = defineModel<string | undefined>({ default: undefined })
</script>

<style scoped>
.ui-select {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text);
  transition:
    border-color var(--transition-base) ease,
    box-shadow var(--transition-base) ease;
}

.ui-select--sm {
  min-height: 28px;
}

.ui-select--md {
  min-height: 34px;
}

.ui-select:hover:not(.is-disabled) {
  border-color: var(--color-border-strong);
}

.ui-select:focus-within {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-focus);
}

.ui-select.is-invalid {
  border-color: var(--color-danger);
}

.ui-select.is-disabled {
  background: var(--color-surface-sunken);
  opacity: 0.7;
}

.ui-select__field {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 28px 0 var(--space-3);
  border: 0;
  outline: none;
  background: transparent;
  box-shadow: none;
  appearance: none;
  font: inherit;
  font-size: var(--text-sm);
  color: inherit;
  cursor: pointer;
}

.ui-select--sm .ui-select__field {
  padding-left: var(--space-2);
}

.ui-select__field:focus,
.ui-select__field:focus-visible {
  outline: none;
  box-shadow: none;
}

.ui-select__field:disabled {
  cursor: not-allowed;
}

.ui-select__chevron {
  position: absolute;
  right: 10px;
  color: var(--color-text-muted);
  pointer-events: none;
}
</style>
